import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { API_URL } from "../../config.js";
import Notification from "../../components/Notification.js";
import FarmersDropdownField from "../../components/FarmersDropdownField.js";
import ProductsDropdownField from "../../components/ProductsDropdownField.js";
import { getLocalDate } from "../../utils/TextUtils.js"; 

function DebtForm() {
    const [debt, setDebt] = useState({
        farmer_id: "",
        product_id: "",
        amount: "",
        price_per_item: "",
        no_of_items: 1,
        date_taken: getLocalDate()
    });

    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [isEditing, setIsEditing] = useState(false);
    const navigate = useNavigate();
    const { id } = useParams();
    const token = localStorage.getItem("access_token");
    const [allFarmers, setAllFarmers] = useState([]);
    const [allProducts, setAllProducts] = useState([]);

    // Calculate amount whenever price_per_item or no_of_items changes
    useEffect(() => {
        const price = parseFloat(debt.price_per_item) || 0;
        const quantity = parseInt(debt.no_of_items) || 1;
        const calculatedAmount = (price * quantity).toFixed(2);

        setDebt(prev => ({
            ...prev,
            amount: calculatedAmount
        }));
    }, [debt.price_per_item, debt.no_of_items]);

    useEffect(() => {
        const fetchDebt = async () => {
            if (id) {
                try {
                    const response = await fetch(`${API_URL}/debts/${id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    if (response.ok) {
                        const data = await response.json();
                        setDebt({
                            ...data,
                            price_per_item: data.price_per_item || data.amount,
                            no_of_items: data.no_of_items || 1,
                            date_taken: data.date_taken || new Date().toISOString().split('T')[0]
                        });
                        setIsEditing(true);
                    } else if (response.status === 404) {
                        setNotification({ type: "danger", message: "Debt not found." });
                    } else if (response.status === 401) {
                        navigate("/logout");
                    } else {
                        throw new Error("Failed to fetch debt.");
                    }
                } catch (error) {
                    setNotification({ type: "danger", message: "An error occurred while fetching the debt." });
                }
            }
        };

        const fetchAllFarmers = async () => {
            try {
                const response = await fetch(`${API_URL}/farmers?paginate=false`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (response.ok) {
                    const data = await response.json();
                    setAllFarmers(data || []);
                } else {
                    throw new Error("Failed to fetch farmers.");
                }
            } catch (error) {
                setNotification({ type: "danger", message: "An error occurred while fetching farmers." });
            }
        };

        const fetchAllProducts = async () => {
            try {
                const response = await fetch(`${API_URL}/products?paginate=false`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (response.ok) {
                    const data = await response.json();
                    setAllProducts(data || []);
                } else {
                    throw new Error("Failed to fetch products.");
                }
            } catch (error) {
                setNotification({ type: "danger", message: "An error occurred while fetching products." });
            }
        };

        const fetchData = async () => {
            await fetchDebt();
            await fetchAllFarmers();
            await fetchAllProducts();
            setLoading(false);
        };

        fetchData();
    }, [id, navigate, token]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setDebt((prevDebt) => ({
            ...prevDebt,
            [name]: value,
        }));
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    };

    const handleProductChange = async (event) => {
        const selectedProductId = event.target.value;
        setDebt((prevDebt) => ({
            ...prevDebt,
            product_id: selectedProductId,
        }));

        if (selectedProductId) {
            try {
                const response = await fetch(`${API_URL}/products/${selectedProductId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (response.ok) {
                    const productData = await response.json();
                    setDebt((prevDebt) => ({
                        ...prevDebt,
                        price_per_item: productData.product.price || "",
                        no_of_items: 1, // Reset quantity when product changes
                    }));
                } else {
                    throw new Error("Failed to fetch product details.");
                }
            } catch (error) {
                console.error("Error fetching product price:", error);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});

        try {
            const method = isEditing ? "PUT" : "POST";
            const url = isEditing ? `${API_URL}/debts/${id}` : `${API_URL}/debts`;

            const response = await fetch(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(debt),
            });

            if (response.ok) {
                setNotification({ message: isEditing ? "Debt successfully updated." : "Debt successfully created.", type: "success" });
                setTimeout(() => {
                    navigate(isEditing ? `/dashboard/debts/${id}/details` : "/dashboard/debts");
                }, 1500);
            } else if (response.status === 401) {
                navigate("/logout");
            } else if (response.status === 422) {
                const errorData = await response.json();
                setErrors(errorData.errors || {});
                setNotification({ message: "Please fix the errors in the form.", type: "danger" });
            } else {
                try {
                    const errorData = await response.json(); setErrors(errorData);
                    setNotification({ message: errorData.message || "An error occurred.", type: "danger" });
                } catch (jsonError) {
                    console.error("Failed to parse error JSON:", jsonError);
                    setNotification({ message: `Unexpected error: ${response.statusText}`, type: "danger" });
                }
            }

        } catch (error) {
            setNotification({ message: "An error occurred while saving the debt.", type: "danger" });
        } finally {
            setLoading(false);
        }
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <p className="title">{isEditing ? "Update Debt" : "Add Debt"}</p>
                    </div>
                </div>
                <div className="level-right">
                    <p className="level-item">
                        <Link to="/dashboard/debts" className="button primary-bg">
                            <i className="fa fa-arrow-left" aria-hidden="true"></i> &nbsp; Back
                        </Link>
                    </p>
                </div>
            </nav>

            <div className="column dash-section">
                {notification.message && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                <form onSubmit={handleSubmit}>
                    <div className="columns">
                        <div className="column is-half">
                            {/* Farmer Dropdown - Readonly when editing */}
                            <div className="field">
                                <label className="label">Farmer</label>
                                {isEditing ? (
                                    <input
                                        className="input"
                                        type="text"
                                        value={debt.farmer ? `${debt.farmer.first_name} ${debt.farmer.last_name} - (${debt.farmer.farmer_number})` : 'Loading farmer...'}
                                        readOnly
                                    />
                                ) : (
                                    <div className="control">
                                        <FarmersDropdownField
                                            name="farmer_id"
                                            value={debt.farmer_id}
                                            onChange={handleChange}
                                            options={allFarmers}
                                            error={errors.farmer_id}
                                            placeholder="Select a farmer"
                                            required
                                        />
                                    </div>
                                )}
                                {errors.farmer_id && errors.farmer_id.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>

                            {/* Product Dropdown */}
                            <div className="field">
                                <label className="label">Product</label>
                                <div className="control">
                                    <ProductsDropdownField
                                        name="product_id"
                                        value={debt.product_id}
                                        onChange={handleProductChange}
                                        options={allProducts}
                                        error={errors.product_id}
                                        placeholder="Select a product"
                                        required
                                    />
                                </div>
                                {errors.product_id && errors.product_id.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>

                            {/* Date Taken Field */}
                            <div className="field">
                                <label className="label">Date Taken</label>
                                <div className="control">
                                    <input
                                        className={`input ${errors.date_taken ? "is-danger" : ""}`}
                                        type="date"
                                        name="date_taken"
                                        value={debt.date_taken}
                                        onChange={handleChange}
                                        max={getLocalDate()}
                                        required
                                    />
                                </div>
                                {errors.date_taken && errors.date_taken.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>

                            {/* Price Per Item Field */}
                            <div className="field">
                                <label className="label">Price Per Item</label>
                                <div className="control">
                                    <input
                                        className={`input ${errors.price_per_item ? "is-danger" : ""}`}
                                        type="number"
                                        name="price_per_item"
                                        value={debt.price_per_item}
                                        onChange={handleChange}
                                        placeholder="Price per item"
                                        step="0.01"
                                        min="1"
                                        required
                                    />
                                </div>
                                {errors.price_per_item && errors.price_per_item.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>

                            {/* Number of Items Field */}
                            <div className="field">
                                <label className="label">Number of Items</label>
                                <div className="control">
                                    <input
                                        className={`input ${errors.no_of_items ? "is-danger" : ""}`}
                                        type="number"
                                        name="no_of_items"
                                        value={debt.no_of_items}
                                        onChange={handleChange}
                                        placeholder="Number of items"
                                        min="1"
                                        required
                                    />
                                </div>
                                {errors.no_of_items && errors.no_of_items.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>

                            {/* Total Amount Field (readonly) */}
                            <div className="field">
                                <label className="label">Total Amount</label>
                                <div className="control">
                                    <input
                                        className="input"
                                        type="number"
                                        name="amount"
                                        value={debt.amount}
                                        readOnly
                                    />
                                </div>
                            </div>


                            {/* Serial Number Field */}
                            <div className="field">
                                <label className="label">Serial Number</label>
                                <div className="control">
                                    <input
                                        className={`input ${errors.serial_number ? "is-danger" : ""}`}
                                        type="text"
                                        name="serial_number"
                                        value={debt.serial_number || ""}
                                        onChange={handleChange}
                                        placeholder="Serial number (optional)"
                                    />
                                </div>
                                {errors.serial_number && errors.serial_number.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>
                            
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="button primary-bg is-fullwidth"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="icon">
                                    <i className="fas fa-spinner fa-spin"></i>
                                </span>
                                <span>LOADING...</span>
                            </>
                        ) : (
                            isEditing ? "UPDATE DEBT" : "ADD DEBT"
                        )}
                    </button>
                </form>
            </div>
        </main>
    );
}

export default DebtForm;